/**
 * ID enums, corresponding to the ids of the items in each of the csv files
 */
export enum RegionIds {
  MELANESIA = 1,
  POLYNESIA = 2,
  MICRONESIA = 3
}

/**
 * TypeScript enums for CSV column headers
 * Maps verbose user-facing column names to concise enum keys
 */

// Islands CSV Headers
export enum IslandHeaders {
  ID = "Island ID",
  NAME = "Name",
  INDIGENOUS_NAME = "Indigenous Name",
  MAX_ASSIGNED_STUDENTS = "Max Assigned Students"
}

// Fact Sheet Fields
// Enum of field names that students populate when creating fact sheets for their assigned islands
export enum FactSheetFields {
  WESTERN_NAME = "Western Name",
  INDIGENOUS_NAME = "Indigenous Name",
  INDIGENOUS_LANGUAGE = "Indigenous Language",
  PRIMARY_LANGUAGE = "Primary Language",
  POPULATION_SIZE = "Population Size",
  CULTURAL_REGION = "Cultural Region",
  CAPITAL = "Capital",
  POLITICAL_STRUCTURE = "Political Structure",
  NATIONAL_AND_OR_INDIGENOUS_FEATURES_AND_CHARACTERISTICS = "National and/or Indigenous Features and Characteristics"
}

// Island Assignment Status
// Enum for island assignment status states
export enum IslandStatus {
  AVAILABLE = "available",
  PARTIALLY_ASSIGNED = "partial",
  FULLY_ASSIGNED = "full"
}

/**
 * Student Entry Structure, as stored in the datastore
 */
export type StudentEntry = {
  onid: string;
  fullname: string;
  value: StudentEntryValue;
};

/**
 * Student Entry Value Structure
 */
export type StudentEntryValue = {
  IslandId: string;
  ReportDetails: ReportDetails;
};

/**
 * Type for report details
 * each one is optional since students can save their report details as they go
 */
export type ReportDetails = {
  [FactSheetFields.WESTERN_NAME]?: string;
  [FactSheetFields.INDIGENOUS_NAME]?: string;
  [FactSheetFields.INDIGENOUS_LANGUAGE]?: string;
  [FactSheetFields.PRIMARY_LANGUAGE]?: string;
  [FactSheetFields.POPULATION_SIZE]?: string;
  [FactSheetFields.CULTURAL_REGION]?: string;
  [FactSheetFields.CAPITAL]?: string;
  [FactSheetFields.POLITICAL_STRUCTURE]?: string;
  [FactSheetFields.NATIONAL_AND_OR_INDIGENOUS_FEATURES_AND_CHARACTERISTICS]?: string[];
};

/**
 * Type for a student report
 * Represents the structure of student report data mapped to the enriched island data
 */
export type StudentReport = {
  onid: string;
  fullName: string;
  reportDetails: ReportDetails;
};

/**
 * Type for enriched island data
 * Combines CSV island data with student-populated fact sheet data
 *
 * Structure:
 * - Required CSV fields: Island ID, Name, Indigenous Name, Max Assigned Students
 * - Array of StudentReports
 *
 * Note: Both IslandHeaders.INDIGENOUS_NAME and FactSheetFields.INDIGENOUS_NAME
 * have the same string value "Indigenous Name", so fact sheet data can override CSV data.
 */
export type EnrichedIsland = {
  // Required CSV fields
  [IslandHeaders.ID]: string;
  [IslandHeaders.NAME]: string;
  [IslandHeaders.INDIGENOUS_NAME]: string;
  [IslandHeaders.MAX_ASSIGNED_STUDENTS]: string;
  Reports: StudentReport[];
};
